import React from "react";
import { useTranslation } from "react-i18next";
import {
  useAuthentication,
  useExportData,
  useProjectGanttRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveWbsConfiguration,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { ExportsDataTypes, ExportsFormats } from "@planzone/dto";

import { useRoles } from "@scenes/safeguard";

import { Props } from "./types";
import { Loader } from "./loader";
import styles from "./styles.scss";
import { useUtils } from "@common/treeToggle";

function ExportButton(props: Props) {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = React.useState(false);
  const { planzone } = useRetrieveCurrentPlanzone();
  const { account } = useAuthentication();
  const { exportData } = useExportData();
  const { mappedEl } = useUtils();
  const roles = useRoles({});
  const { isRequestPending, gantt, retrieveProjectGantt } =
    useProjectGanttRetrieval(props.project);
  const { configuration } = useRetrieveWbsConfiguration(planzone);
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    props.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];

  useMessage((event) => {
    if (isExporting && event.type === events.retrieveProjectGanttSucceeded) {
      exportData({
        fileName: planzone.name,
        author: account,
        data: {
          type: ExportsDataTypes.OPTIMIZED_WBS_REPORT,
          data: {
            planzone,
            projects: [props.project],
            roles,
            configuration,
            template,
            toggledLists: Object.entries(mappedEl).reduce(
              (acc, [key, { isOpen }]) => ({ ...acc, [key]: isOpen }),
              {},
            ),
            filter: props.filter,
            sort: props.sort,
          },
        },
        format: ExportsFormats.EXCEL,
      });
      setIsExporting(false);
    }
  });

  return (
    <button
      className={styles.exportButton}
      onClick={() => {
        if (!isRequestPending) {
          setIsExporting(true);
          retrieveProjectGantt(props.project);
        }
      }}
    >
      {isRequestPending ? (
        <div className={styles.icon}>
          <Loader />
        </div>
      ) : (
        <svg className={styles.icon} viewBox="0 0 23.57 20.953">
          <path d="M15.715,4.989a.979.979,0,0,0-.286-.692L11.422.286a.981.981,0,0,0-.7-.286h-.25V5.238h5.238ZM23.368,12.6,19.451,8.66a.656.656,0,0,0-1.121.462v2.664H15.711v2.619H18.33v2.668a.656.656,0,0,0,1.121.462l3.916-3.949A.7.7,0,0,0,23.368,12.6ZM7.857,13.751v-1.31a.657.657,0,0,1,.655-.655h7.2V6.548H10.149a.985.985,0,0,1-.982-.982V0H.982A.98.98,0,0,0,0,.982V19.971a.98.98,0,0,0,.982.982H14.733a.98.98,0,0,0,.982-.982V14.405h-7.2A.657.657,0,0,1,7.857,13.751Z" />
        </svg>
      )}
      <p className={styles.text}>{t("planzoneMembersPage.export")}</p>
    </button>
  );
}

export { ExportButton };
